import { useQuery } from "@tanstack/react-query";
import createPrivateAdminPanelInstance from "../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../api/ApiRoutes";
import { bonusAdminBody } from "./bonusAdminBody";
import type {
  BrazeSendType,
  BrazeSeriesPayloadOut,
} from "../../components/Promotions/DynamicBonuses/types";

type StoredToken = { AccessToken?: string };

type SeriesBrazePayloadOptions = {
  expiryHours: number;
  sendType: BrazeSendType;
  enabled: boolean;
};

async function fetchSeriesBrazePayload(
  seriesId: string,
  expiryHours: number,
  sendType: BrazeSendType,
): Promise<BrazeSeriesPayloadOut> {
  const tokenString = localStorage.getItem("token");
  const token: StoredToken | null = tokenString ? JSON.parse(tokenString) : null;
  const instance = createPrivateAdminPanelInstance(token?.AccessToken);
  const url = API_ROUTES.GET_DYNAMIC_BONUS_SERIES_BRAZE_PAYLOAD.replace(
    "{series_id}",
    seriesId,
  );
  const response = await instance.get(url, {
    params: { expiry_hours: expiryHours, send_type: sendType },
  });
  const body = bonusAdminBody<BrazeSeriesPayloadOut>(response);
  if (!body) {
    throw new Error("Braze payload not found");
  }
  return body;
}

export const getSeriesBrazePayloadQueryKey = (
  seriesId: string,
  expiryHours: number,
  sendType: BrazeSendType,
) =>
  ["GET_DYNAMIC_BONUS_SERIES_BRAZE_PAYLOAD", seriesId, expiryHours, sendType] as const;

export const useSeriesBrazePayload = (
  seriesId: string,
  { expiryHours, sendType, enabled }: SeriesBrazePayloadOptions,
) => {
  return useQuery<BrazeSeriesPayloadOut>({
    queryKey: getSeriesBrazePayloadQueryKey(seriesId, expiryHours, sendType),
    queryFn: () => fetchSeriesBrazePayload(seriesId, expiryHours, sendType),
    enabled: enabled && Boolean(seriesId),
    staleTime: 1000 * 30,
    refetchOnWindowFocus: false,
  });
};
